import React from 'react'
import ReactDOM from 'react-dom/client'
import { ClerkProvider } from '@clerk/clerk-react'
import { BrowserRouter } from 'react-router-dom'
import AppRouter from './AppRouter.tsx'
import './index.css'

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable')
}

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.log('SW registration failed:', err);
    });
  });
}

// PWA install prompt
let deferredPrompt: Event | null = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  // Show install banner
  const banner = document.createElement('div');
  banner.id = 'pwa-install-banner';
  banner.innerHTML = `
    <div style="position:fixed;bottom:0;left:0;right:0;background:rgba(18,18,26,0.95);backdrop-filter:blur(12px);color:#f0f0f5;padding:14px 16px;display:flex;align-items:center;justify-content:space-between;z-index:9999;font-family:'Inter',sans-serif;border-top:1px solid rgba(255,255,255,0.1);">
      <span style="font-size:0.9em;">📱 Add Homework Tracker to your home screen</span>
      <div style="display:flex;gap:8px;">
        <button id="pwa-install-btn" style="background:linear-gradient(135deg,#7c3aed,#f59e0b);color:white;border:none;padding:8px 16px;border-radius:8px;font-weight:600;cursor:pointer;font-family:inherit;">Install</button>
        <button id="pwa-dismiss-btn" style="background:rgba(255,255,255,0.08);color:#8b8b9e;border:1px solid rgba(255,255,255,0.1);padding:8px 12px;border-radius:8px;cursor:pointer;">✕</button>
      </div>
    </div>
  `;
  document.body.appendChild(banner);
  document.getElementById('pwa-install-btn')?.addEventListener('click', async () => {
    (deferredPrompt as any)?.prompt();
    deferredPrompt = null;
    banner.remove();
  });
  document.getElementById('pwa-dismiss-btn')?.addEventListener('click', () => banner.remove());
});

const clerkAppearance = {
  baseTheme: undefined,
  variables: {
    colorPrimary: '#7c3aed',
    colorBackground: '#12121a',
    colorText: '#f0f0f5',
    colorTextSecondary: '#8b8b9e',
    colorInputBackground: 'rgba(255,255,255,0.07)',
    colorInputText: '#f0f0f5',
    borderRadius: '12px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  elements: {
    card: {
      backgroundColor: '#12121a',
      border: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    },
    headerTitle: {
      color: '#f0f0f5',
    },
    headerSubtitle: {
      color: '#8b8b9e',
    },
    socialButtonsBlockButton: {
      backgroundColor: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.1)',
      color: '#f0f0f5',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.1)',
      },
    },
    formFieldInput: {
      backgroundColor: 'rgba(255,255,255,0.07)',
      border: '1px solid rgba(255,255,255,0.1)',
      color: '#f0f0f5',
      '&:focus': {
        borderColor: '#7c3aed',
        boxShadow: '0 0 0 3px rgba(124,58,237,0.25)',
      },
    },
    formButtonPrimary: {
      background: 'linear-gradient(135deg, #7c3aed 0%, #f59e0b 100%)',
      border: 'none',
      '&:hover': {
        background: 'linear-gradient(135deg, #6d28d9 0%, #d97706 100%)',
      },
    },
    footerActionLink: {
      color: '#a78bfa',
    },
    identityPreviewEditButton: {
      color: '#a78bfa',
    },
    formFieldLabel: {
      color: '#8b8b9e',
    },
    dividerLine: {
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
    dividerText: {
      color: '#5a5a6e',
    },
    userButtonPopoverCard: {
      backgroundColor: '#12121a',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    userButtonPopoverActionButton: {
      color: '#f0f0f5',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.05)',
      },
    },
    userButtonPopoverFooter: {
      borderTop: '1px solid rgba(255,255,255,0.1)',
    },
  },
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} appearance={clerkAppearance}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </ClerkProvider>
  </React.StrictMode>,
)
